import { TooltipProvider } from "@/components/ui/tooltip";
import { CornerUpRight, Minus, MoveUpRight } from "lucide-react";
import { useCallback, useEffect, useRef } from "react";
import { useCanvasToolbarLayout } from "../context/CanvasToolbarLayoutContext";
import { useCanvasShapeAndEdgeStyle } from "../hooks/useCanvasShapeAndEdgeStyle";
import { type ConnectorPreset } from "../hooks/useConnectorPlacement";
import {
  ALL_SHAPE_TYPES,
  SHAPE_LABELS,
  type ShapeType,
} from "../nodes/shapes/index";
import { SHAPE_COLORS } from "../nodes/shared/CanvasColorPalettes";
import { CanvasColorPickerButton } from "../nodes/shared/CanvasColorPickerButton";
import {
  LineStylePicker,
  type ShapeLineStyle,
} from "../nodes/shared/LineStylePicker";
import {
  CanvasToolbarButton,
  CanvasToolbarDivider,
  CanvasToolbarGroup,
  CanvasToolbarShell,
} from "./toolbar";
import { SHAPE_LUCIDE_ICON } from "./toolbar/canvasToolbarIcon";

interface ShapesPopoverProps {
  activeShape: ShapeType | null;
  activeConnectorPreset: ConnectorPreset | null;
  onShapeSelect: (shape: ShapeType) => void;
  onConnectorSelect: (preset: ConnectorPreset) => void;
  /** Called when Escape is pressed and no shape placement is in progress. */
  onClose: () => void;
}

/**
 * Shape and connector picker — same shell + button sizing as the primary toolbar.
 */
export function ShapesPopover({
  activeShape,
  activeConnectorPreset,
  onShapeSelect,
  onConnectorSelect,
  onClose,
}: ShapesPopoverProps) {
  const {
    flyoutOrientation,
    tooltipSide,
    popoverSide,
    popoverSideOffset,
    swatchOrientation,
  } = useCanvasToolbarLayout();
  const {
    activeShapeColor,
    activeShapeNoFill,
    activeShapeStrokeStyle,
    activeShapeStrokeWidth,
    setShapeStyle,
  } = useCanvasShapeAndEdgeStyle();
  const isColorOpenRef = useRef(false);
  const isLineStyleOpenRef = useRef(false);

  const handleColorOpenChange = useCallback((open: boolean) => {
    isColorOpenRef.current = open;
  }, []);

  const handleLineStyleOpenChange = useCallback((open: boolean) => {
    isLineStyleOpenRef.current = open;
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (isColorOpenRef.current || isLineStyleOpenRef.current) return;
      onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  // Design-system order: the shape family leads, connector lines second,
  // the style pickers (a web-ui extra the spec doesn't carry) tail the bar.
  return (
    <TooltipProvider>
      <CanvasToolbarShell orientation={flyoutOrientation} aria-label="Shapes">
        <CanvasToolbarGroup orientation={flyoutOrientation}>
          {ALL_SHAPE_TYPES.map((shapeType) => (
            <ShapeToolbarButton
              key={shapeType}
              shapeType={shapeType}
              isActive={activeShape === shapeType}
              tooltipSide={tooltipSide}
              onClick={() => onShapeSelect(shapeType)}
            />
          ))}
        </CanvasToolbarGroup>

        <CanvasToolbarDivider orientation={flyoutOrientation} />

        <CanvasToolbarGroup orientation={flyoutOrientation}>
          {CONNECTOR_PRESETS.map((preset) => {
            const isActive =
              activeConnectorPreset?.arrowMode === preset.arrowMode &&
              activeConnectorPreset?.curve_type === preset.curve_type;
            return (
              <CanvasToolbarButton
                key={preset.label}
                label={preset.label}
                tooltipSide={tooltipSide}
                active={isActive}
                onClick={() => onConnectorSelect(preset)}
              >
                {preset.icon}
              </CanvasToolbarButton>
            );
          })}
        </CanvasToolbarGroup>

        <CanvasToolbarDivider orientation={flyoutOrientation} />

        <CanvasToolbarGroup orientation={flyoutOrientation}>
          <CanvasColorPickerButton
            colors={SHAPE_COLORS}
            activeValue={activeShapeColor}
            noFill={activeShapeNoFill}
            tooltip="Color"
            onColorChange={(color) => setShapeStyle({ color })}
            onNoFillChange={(nextNoFill) =>
              setShapeStyle({ noFill: nextNoFill })
            }
            onOpenChange={handleColorOpenChange}
            popoverSide={popoverSide}
            popoverSideOffset={popoverSideOffset}
            swatchOrientation={swatchOrientation}
          />

          <LineStylePicker
            includeNone
            value={{
              strokeStyle: activeShapeStrokeStyle,
              strokeWidth: activeShapeStrokeWidth,
            }}
            onChange={(ls: ShapeLineStyle) => {
              setShapeStyle({
                strokeStyle: ls.strokeStyle,
                strokeWidth: ls.strokeWidth,
              });
            }}
            onOpenChange={handleLineStyleOpenChange}
            popoverSide={popoverSide}
            popoverSideOffset={popoverSideOffset}
            tooltip="Stroke"
          />
        </CanvasToolbarGroup>
      </CanvasToolbarShell>
    </TooltipProvider>
  );
}

function ShapeToolbarButton({
  shapeType,
  isActive,
  tooltipSide,
  onClick,
}: {
  shapeType: ShapeType;
  isActive: boolean;
  tooltipSide: "top" | "right" | "bottom" | "left";
  onClick: () => void;
}) {
  const Icon = SHAPE_LUCIDE_ICON[shapeType];
  return (
    <CanvasToolbarButton
      label={SHAPE_LABELS[shapeType]}
      tooltipSide={tooltipSide}
      active={isActive}
      onClick={onClick}
    >
      <Icon className="size-4" strokeWidth={2} />
    </CanvasToolbarButton>
  );
}

interface ConnectorPresetDef extends ConnectorPreset {
  label: string;
  icon: React.ReactNode;
}

const CONNECTOR_PRESETS: ConnectorPresetDef[] = [
  {
    label: "Straight arrow",
    arrowMode: "end",
    curve_type: "straight",
    icon: <MoveUpRight className="size-4" strokeWidth={2} />,
  },
  {
    label: "Curved arrow",
    arrowMode: "end",
    curve_type: "smooth",
    icon: (
      <svg className="size-4" viewBox="0 0 16 16" fill="none" aria-hidden>
        <path
          d="M 2 13 Q 3 4 12 4"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          fill="none"
        />
        <polyline
          points="9,1 12,4 9,7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    label: "Step arrow",
    arrowMode: "end",
    curve_type: "step",
    icon: <CornerUpRight className="size-4" strokeWidth={2} />,
  },
  {
    label: "Line",
    arrowMode: "none",
    curve_type: "straight",
    icon: <Minus className="size-4" strokeWidth={2} />,
  },
];
